import React, { useState } from 'react';
import { Crown, Sparkles, Loader, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const UpgradeBanner = ({ 
  variant = 'header', // 'header' (slim top bar), 'card' (profile page card), 'inline' (inline button)
  onClose = null, // Optional close handler for dismissible banners
  comingSoon = false, // If true, show "Coming Soon" modal instead of checkout
  className = '' 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  const handleUpgrade = async () => {
    // If comingSoon mode, show modal instead of checkout
    if (comingSoon) {
      setShowComingSoonModal(true);
      return;
    }

    if (!user) {
      setError('Please log in to upgrade');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/stripe?action=checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          returnUrl: window.location.origin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to start checkout');
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: '🔓', text: 'Unlimited AI queries' },
    { icon: '🔔', text: 'Real-time perk alerts' },
    { icon: '⏰', text: 'Deal expiration reminders' },
    { icon: '💰', text: 'Priority savings recommendations' },
  ];

  // Coming Soon Modal
  const ComingSoonModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
          <Crown className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium Coming Soon! 🚀</h2>
        <p className="text-gray-600 mb-6">
          We're putting the finishing touches on Premium subscriptions. You'll be the first to know when it launches!
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowComingSoonModal(false)}
            className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              setShowComingSoonModal(false);
              // Could trigger email signup here in the future
            }}
            className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            Got It!
          </button>
        </div>
      </div>
    </div>
  );

  // Tooltip - only show on larger screens (hidden on mobile to prevent overflow)
  const TooltipContent = () => (
    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 hidden sm:block">
      <div className="bg-gray-900 text-white rounded-lg shadow-xl p-4 w-64">
        {/* Arrow */}
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-900"></div>
        
        <p className="font-semibold text-purple-300 mb-2">Premium Benefits:</p>
        <ul className="space-y-2">
          {benefits.map((benefit, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span>{benefit.icon}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 pt-2 border-t border-gray-700 text-center">
          <span className="text-lg font-bold text-green-400">$4.99</span>
          <span className="text-gray-400">/month</span>
        </p>
      </div>
    </div>
  );

  // Header variant - slim bar at top of page (mobile responsive)
  if (variant === 'header') {
    return (
      <div className={`relative ${className}`}>
        {showComingSoonModal && <ComingSoonModal />}
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-gray-900 font-semibold px-2.5 py-1 sm:px-4 sm:py-1.5 rounded-full shadow-md hover:shadow-lg transition-all text-xs sm:text-sm disabled:opacity-50"
          >
            {loading ? (
              <Loader className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
            ) : (
              <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
            {/* Short text on mobile, full text on larger screens */}
            <span className="sm:hidden">Upgrade</span>
            <span className="hidden sm:inline">Upgrade to Premium</span>
          </button>
          
          {showTooltip && <TooltipContent />}
        </div>
        
        {error && (
          <p className="absolute top-full left-0 mt-1 text-xs text-red-400 bg-red-900/50 px-2 py-1 rounded whitespace-nowrap">
            {error}
          </p>
        )}
      </div>
    );
  }

  // Card variant - for profile page (mobile responsive)
  if (variant === 'card') {
    return (
      <div className={`bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-4 sm:p-5 text-white shadow-lg ${className}`}>
        {showComingSoonModal && <ComingSoonModal />}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
            <h3 className="font-bold text-base sm:text-lg">Upgrade to Premium</h3>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        <ul className="space-y-1.5 sm:space-y-2 mb-4">
          {benefits.map((benefit, i) => (
            <li key={i} className="flex items-center gap-2 text-xs sm:text-sm text-purple-100">
              <span>{benefit.icon}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        
        {/* Stack price and button on mobile, side by side on larger screens */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <span className="text-xl sm:text-2xl font-bold">$4.99</span>
            <span className="text-purple-200">/month</span>
          </div>
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-purple-700 font-bold px-5 py-2 rounded-lg shadow transition-all disabled:opacity-50 w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </>
            )}
          </button>
        </div>
        
        {error && (
          <p className="mt-2 text-sm text-red-200 bg-red-900/30 px-3 py-1 rounded">
            {error}
          </p>
        )}
      </div>
    );
  }

  // Inline variant - simple button with tooltip (mobile responsive)
  return (
    <div 
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {showComingSoonModal && <ComingSoonModal />}
      <button
        onClick={handleUpgrade}
        disabled={loading}
        className="flex items-center gap-1.5 sm:gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-sm"
      >
        {loading ? (
          <Loader className="w-4 h-4 animate-spin" />
        ) : (
          <Crown className="w-4 h-4" />
        )}
        <span>Go Premium</span>
      </button>
      
      {showTooltip && <TooltipContent />}
      
      {error && (
        <p className="absolute top-full left-0 mt-1 text-xs text-red-600 whitespace-nowrap">
          {error}
        </p>
      )}
    </div>
  );
};

// Premium badge for showing subscription status (mobile responsive)
export const PremiumBadge = ({ className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 text-xs font-bold rounded-full shadow ${className}`}>
    <Crown className="w-3 h-3" />
    <span className="hidden sm:inline">PREMIUM</span>
    <span className="sm:hidden">PRO</span>
  </span>
);

// Manage subscription button for premium users
export const ManageSubscriptionButton = ({ className = '' }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleManage = async () => {
    if (!user) return;

    setLoading(true);

    try {
      const response = await fetch('/api/stripe?action=portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          returnUrl: window.location.href,
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleManage}
      disabled={loading}
      className={`text-sm text-purple-600 hover:text-purple-800 underline transition-colors disabled:opacity-50 ${className}`}
    >
      {loading ? 'Loading...' : 'Manage Subscription'}
    </button>
  );
};

export default UpgradeBanner;